import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'

import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { TruncatedText } from '@/components/truncated-text/TruncatedText'
import { OverflowMenu } from '@/assets/icons'
import type { HeaderProps } from './types'
export type { HeaderMenuFooter } from './types'
import { HEADER_HEIGHT_PX, MOBILE_HEADER_HEIGHT_PX } from './constants'
import { DEVICE_TYPE, useHeaderDeviceType } from './hooks/useHeaderDeviceType'

export type { HeaderProps } from './types'

/** Character cap on the breadcrumbs before the active one; the active crumb fills the space and truncates by width. */
const kBreadcrumbMaxLabelLength = 60

/**
 * Page or section header: a title or breadcrumb trail on the left, and custom content plus an optional overflow menu
 * on the right.
 */
export function Header({
  startContent,
  title,
  titleSize = 'xl',
  breadcrumbs,
  endContent,
  menuItems,
  menuDisabled = false,
  menuAriaLabel = 'Menu options',
  menuFooter,
  endInset,
  borderBottom = true,
}: HeaderProps) {
  const deviceType = useHeaderDeviceType()
  const isMobile = deviceType !== DEVICE_TYPE.DESKTOP
  const hasBreadcrumbs = breadcrumbs != null && breadcrumbs.length > 0
  // A plain string renders as the styled <h1>;
  // any other ReactNode is custom title-area content, rendered as-is.
  const titleText = typeof title === 'string' ? title : undefined
  const hasTitleText = !hasBreadcrumbs && titleText != null && titleText !== ''
  const hasCustomTitle = !hasBreadcrumbs && title != null && titleText == null
  const hasStart = startContent != null || hasBreadcrumbs || hasTitleText || hasCustomTitle
  const hasMenu = menuItems != null && menuItems.length > 0
  const hasEnd = endContent != null || hasMenu
  // A title with nothing beside it keeps the wider inset
  const inset = endInset ?? (hasEnd ? 'icon' : 'button')

  return (
    <header
      data-ds-component="Header"
      data-ds-title-size={titleSize}
      className={clsx(
        'flex flex-row items-center relative',
        // Match chat's 16px gutter and 32px total title inset, including custom titles.
        isMobile ? 'w-full' : 'ml-4 w-auto',
        isMobile ? 'pl-4 py-3' : 'pl-4 py-4',
        // An icon button at the edge carries its own padding, so it gets less.
        inset === 'icon' ? (isMobile ? 'pr-2' : 'pr-5') : isMobile ? 'pr-4' : 'pr-10',
        hasStart ? 'gap-2 justify-between' : 'justify-end',
        isMobile ? 'bg-surface-subtle' : 'bg-surface-content'
      )}
      style={{ height: `${isMobile ? MOBILE_HEADER_HEIGHT_PX : HEADER_HEIGHT_PX}px` }}
    >
      {hasStart && (
        <div className="flex min-w-0 flex-1 items-center">
          {startContent != null && <div className="flex shrink-0 items-center">{startContent}</div>}
          {hasBreadcrumbs && (
            // -ml-2 cancels the crumb's own padding so the first crumb sits flush with the desktop title inset.
            <div className={clsx('min-w-0 flex-1', !isMobile && '-ml-2')}>
              <Breadcrumbs items={breadcrumbs} maxLabelLength={kBreadcrumbMaxLabelLength} persistFirstItem tooltipPlacement="bottom-start" />
            </div>
          )}
          {hasCustomTitle && <h1 className="min-w-0 flex-1">{title}</h1>}
          {hasTitleText && (
            <h1
              className={clsx(
                'min-w-0 font-rethink-sans font-semibold text-text-primary not-italic',
                titleSize === 'sm' ? 'text-sm leading-5 tracking-[-0.15px]' : 'text-xl leading-7 tracking-normal'
              )}
            >
              <TruncatedText placement="bottom-start">{titleText}</TruncatedText>
            </h1>
          )}
        </div>
      )}
      {hasEnd && (
        // The end content keeps its natural width and the title truncates into whatever is left,
        // rather than the buttons compressing and wrapping.
        <div className="flex shrink-0 items-center gap-2">
          {endContent}
          {/* Rendered outside DropdownMenu when disabled — Radix ignores disabled on the trigger with asChild */}
          {hasMenu && menuDisabled && <IconButton variant="plain" color="black" size="lg" icon={<OverflowMenu />} aria-label={menuAriaLabel} disabled />}
          {hasMenu && !menuDisabled && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <IconButton variant="plain" color="black" size="lg" icon={<OverflowMenu />} aria-label={menuAriaLabel} />
              </DropdownMenu.Trigger>
              <MenuContent align="end" sideOffset={4} className="min-w-[160px]">
                <MenuItemGroup>
                  {menuItems.map((item, i) => (
                    <MenuItem
                      key={`${item.label}-${i}`}
                      label={item.label}
                      icon={item.icon}
                      variant={item.destructive ? 'destructive' : 'neutral'}
                      onSelect={() => item.onSelect?.()}
                      disabled={item.disabled}
                    />
                  ))}
                </MenuItemGroup>
                {menuFooter && (
                  <>
                    <MenuSeparator />
                    <DropdownMenu.Item onSelect={menuFooter.onSelect} className="max-w-[280px] cursor-pointer p-4 focus:outline-hidden">
                      {menuFooter.content}
                    </DropdownMenu.Item>
                  </>
                )}
              </MenuContent>
            </DropdownMenu.Root>
          )}
        </div>
      )}
      {/* Bottom border line */}
      {(isMobile || borderBottom) && <div className={clsx('absolute bottom-0 left-0 right-0 bg-border-default', isMobile ? 'h-[0.5px]' : 'h-px')} />}
    </header>
  )
}
